import { v4 as uuidv4 } from '@lukeed/uuid';

import { env } from '@/shared/lib/env';
import { resolveCardCtaVariant } from '@/modules/orders/views/payment-ways/card-cta';

/**
 * Analytics first-party da loja (PRAT-19): manda os eventos do funil para a
 * API da pratiGO, que alimenta "Desempenho da Loja" no gestor.
 *
 * - visitante: id anônimo e permanente do navegador (localStorage);
 * - sessão: um "acesso" — expira com 30 min sem evento, ao trocar de loja ou
 *   quando a pessoa chega de novo por um link com origem (utm, lnk, src...);
 * - origem: lida da URL e do referrer NA CHEGADA, antes de qualquer página
 *   limpar os parâmetros (`?cupom=` e `?ref=` são removidos da URL).
 *
 * Nada aqui pode quebrar o app: toda falha é engolida.
 */

export const STORE_EVENTS = [
  'store_view',
  'category_view',
  'search',
  'product_view',
  'add_to_cart',
  'remove_from_cart',
  'checkout_started',
  'checkout_abandoned',
  'signup',
  'login',
  'order_created',
  // PRAT-111: convite para avaliar visto, com props.channel (a nota a API grava sozinha).
  'rating_prompt_shown',
  // PRAT-83: só para a API de Conversões da Meta (o painel ignora).
  'page_view',
  'payment_info',
  'lead',
] as const;

export type StoreEventName = (typeof STORE_EVENTS)[number];

export type Attribution = Partial<
  Record<
    (typeof ATTRIBUTION_PARAMS)[number] | (typeof CLICK_ID_PARAMS)[number] | 'referrer' | 'app',
    string
  >
>;

type Session = {
  id: string;
  slug: string;
  started: number;
  last: number;
  attribution: Attribution;
};
type Landing = { attribution: Attribution; at: number; explicit: boolean };
type QueuedEvent = {
  slug: string;
  sessionId: string;
  attribution: Attribution;
  event: {
    id: string;
    name: StoreEventName;
    ts: number;
    path: string;
    order_id?: string;
    props?: Props;
  };
};
type Props = Record<string, string | number | boolean | null>;

const VISITOR_KEY = 'pratigo:analytics:visitor';
const SESSION_KEY = 'pratigo:analytics:session';
const LANDING_KEY = 'pratigo:analytics:landing';
export const SESSION_TTL_MS = 30 * 60 * 1000;
const FLUSH_DELAY_MS = 1500;
const ATTRIBUTION_PARAMS = [
  'src',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'lnk',
  'cupom',
  'ref',
  // Anúncio do "Impulsionar minha loja" (id da campanha no Meta Ads da pratiGO).
  'pgo_ad',
] as const;

/**
 * Tags que as próprias redes colam no link: Facebook/Instagram (fbclid),
 * compartilhamento do Instagram (igsh/igshid), Google Ads (gclid/gbraid/wbraid)
 * e TikTok Ads (ttclid). Só a presença importa, então vai "1" — o valor é um
 * identificador do clique, que não guardamos.
 */
const CLICK_ID_PARAMS = [
  'fbclid',
  'igsh',
  'igshid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid',
] as const;

/** App cujo navegador embutido abriu a loja, pelo user agent. */
export function inAppBrowser(userAgent: string): string {
  const ua = userAgent || '';
  if (/Instagram/i.test(ua)) return 'instagram';
  if (/FBAN|FBAV|FB_IAB|FBIOS/i.test(ua)) return 'facebook';
  if (/musical_ly|BytedanceWebview|TikTok/i.test(ua)) return 'tiktok';
  if (/WhatsApp/i.test(ua)) return 'whatsapp';
  return '';
}

/** Rotas do app que não são slug de loja (primeiro segmento da URL). */
const NON_STORE_ROUTES = new Set([
  '',
  'auth',
  'search',
  'profile',
  'indicacao',
  'orders',
  'cart',
  'login-by-link',
  'sentry-test',
  'error-boundary-test',
]);

let queue: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listenersBound = false;

function read<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: Storage, key: string, value: unknown) {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // storage cheio ou bloqueado (aba anônima): segue sem persistir
  }
}

export function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return uuidv4();
  }
}

/** Slug da loja na URL atual, se a página for de loja. */
export function slugFromPath(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  return NON_STORE_ROUTES.has(first) ? '' : decodeURIComponent(first);
}

function currentSlug(): string {
  const fromPath = slugFromPath(window.location.pathname);
  if (fromPath) return fromPath;
  try {
    const stored = sessionStorage.getItem('store-slug');
    return stored && stored !== 'undefined' ? stored : '';
  } catch {
    return '';
  }
}

/** Origem a partir da URL de chegada e do site anterior. */
export function attributionFrom(
  href: string,
  referrer: string,
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent
): Landing | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const attribution: Attribution = {};
  for (const key of ATTRIBUTION_PARAMS) {
    const value = url.searchParams.get(key);
    if (value) attribution[key] = value.slice(0, 200);
  }
  for (const key of CLICK_ID_PARAMS) {
    if (url.searchParams.get(key)) attribution[key] = '1';
  }
  const explicit = Object.keys(attribution).length > 0;

  const app = inAppBrowser(userAgent);
  if (app) attribution.app = app;

  let externalReferrer = '';
  try {
    if (referrer && new URL(referrer).host !== url.host) externalReferrer = referrer.slice(0, 190);
  } catch {
    externalReferrer = '';
  }
  if (externalReferrer) attribution.referrer = externalReferrer;

  if (!explicit && !externalReferrer && !app) return null;
  return { attribution, at: Date.now(), explicit };
}

/**
 * Guarda a origem da chegada. Chamar uma vez, no carregamento do app, antes de
 * qualquer página mexer na URL.
 */
export function captureLanding(href = window.location.href, referrer = document.referrer) {
  const landing = attributionFrom(href, referrer);
  if (landing) write(localStorage, LANDING_KEY, landing);
  captureFbclid(href);
}

/**
 * PRAT-83: identificadores da Meta para a API de Conversões casar o evento do
 * servidor com a pessoa. `_fbp`/`_fbc` são os cookies do Pixel; sem Pixel (ou
 * antes de ele gravar o cookie), o `fbc` é montado do `fbclid` da chegada, no
 * formato que a Meta pede (`fb.1.<ms>.<fbclid>`).
 */
const FBC_KEY = 'pratigo:analytics:fbc';
const FBC_TTL_MS = 90 * 24 * 60 * 60 * 1000;

function captureFbclid(href: string) {
  try {
    const fbclid = new URL(href).searchParams.get('fbclid');
    if (fbclid && /^[\w\-.]{1,400}$/.test(fbclid)) {
      write(localStorage, FBC_KEY, { value: `fb.1.${Date.now()}.${fbclid}`, at: Date.now() });
    }
  } catch {
    // ignora
  }
}

function cookie(name: string): string {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

export function metaIds(now = Date.now()): { fbp?: string; fbc?: string } {
  const ids: { fbp?: string; fbc?: string } = {};
  const fbp = cookie('_fbp');
  if (fbp) ids.fbp = fbp;
  const stored = read<{ value: string; at: number }>(localStorage, FBC_KEY);
  const fbc = cookie('_fbc') || (stored && now - stored.at < FBC_TTL_MS ? stored.value : '');
  if (fbc) ids.fbc = fbc;
  return ids;
}

/** Id do evento: o mesmo vai para o Pixel (eventID) e para a API — a Meta deduplica. */
export function newEventId(): string {
  return uuidv4();
}

/** Sessão (acesso) atual da loja, criando uma nova quando preciso. */
export function getSession(slug: string, now = Date.now()): Session {
  const current = read<Session>(localStorage, SESSION_KEY);
  const landing = read<Landing>(localStorage, LANDING_KEY);
  const freshLanding = landing && now - landing.at < SESSION_TTL_MS ? landing : null;

  const reuse =
    current &&
    current.slug === slug &&
    now - current.last < SESSION_TTL_MS &&
    !(freshLanding?.explicit && freshLanding.at > current.started);

  if (reuse) {
    current.last = now;
    if (freshLanding && freshLanding.at > current.started) {
      // Referrer novo no meio do acesso não troca a origem; só descarta.
      try {
        localStorage.removeItem(LANDING_KEY);
      } catch {
        // ignora
      }
    }
    write(localStorage, SESSION_KEY, current);
    return current;
  }

  const session: Session = {
    id: uuidv4(),
    slug,
    started: now,
    last: now,
    attribution: freshLanding?.attribution ?? {},
  };
  write(localStorage, SESSION_KEY, session);
  try {
    localStorage.removeItem(LANDING_KEY);
  } catch {
    // ignora
  }
  return session;
}

function authToken(): string {
  try {
    const match = document.cookie.match(/(?:^|;\s*)x-auth-token=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

function endpoint(slug: string): string {
  const base = String(env.VITE_API_URL).replace(/\/+$/, '');
  return `${base}/api/app/merchants/${encodeURIComponent(slug)}/analytics/events`;
}

/**
 * Fila de reenvio: todo lote é gravado ANTES de sair e só sai da fila quando a
 * API responde. Rede caiu, servidor fora ou aba fechada no meio: o lote é
 * reenviado na próxima abertura do app (até 1 dia depois). Reenviar um lote que
 * já tinha chegado não distorce o painel — ele conta pessoas e sessões, não
 * eventos, e a API ignora `order_created` repetido.
 */
type OutboxItem = { id: string; slug: string; body: string; at: number; tries: number };
const OUTBOX_KEY = 'pratigo:analytics:outbox';
const OUTBOX_MAX_ITEMS = 30;
const OUTBOX_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const OUTBOX_MAX_TRIES = 5;

function outboxRead(): OutboxItem[] {
  return read<OutboxItem[]>(localStorage, OUTBOX_KEY) ?? [];
}

function outboxUpdate(change: (items: OutboxItem[]) => OutboxItem[]) {
  write(localStorage, OUTBOX_KEY, change(outboxRead()).slice(-OUTBOX_MAX_ITEMS));
}

function deliver(item: OutboxItem) {
  const token = authToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  // 2xx chegou; 4xx (fora o 429) nunca vai passar — os dois saem da fila.
  const settle = (status: number) => {
    if (status < 500 && status !== 429) {
      outboxUpdate(items => items.filter(i => i.id !== item.id));
    }
  };

  // PRAT-83: `keepalive` divide uma cota de 64 KB com tudo que está saindo
  // (o Clarity grava a sessão em paralelo). Cota cheia = "Failed to fetch" sem
  // nem ir para a rede, e o lote ficava parado até a próxima abertura do app
  // (checkout, pagamento e cadastro chegavam atrasados). Tenta de novo sem ele.
  const post = (keepalive: boolean) =>
    fetch(endpoint(item.slug), { method: 'POST', headers, body: item.body, keepalive }).then(
      response => settle(response.status)
    );
  try {
    post(true).catch(() => post(false).catch(() => undefined));
  } catch {
    try {
      if (
        navigator.sendBeacon?.(endpoint(item.slug), new Blob([item.body], { type: 'text/plain' }))
      ) {
        settle(202);
      }
    } catch {
      // sem rede: fica na fila para a próxima abertura
    }
  }
}

function send(slug: string, body: string) {
  const item: OutboxItem = { id: uuidv4(), slug, body, at: Date.now(), tries: 1 };
  outboxUpdate(items => [...items, item]);
  deliver(item);
}

/**
 * Headers do place-order com o visitante e o acesso atuais: a API grava o
 * `order_created` no próprio checkout, então o pedido entra no funil mesmo se
 * o evento do navegador se perder.
 */
export function analyticsOrderHeaders(slug: string): Record<string, string> {
  try {
    if (!slug) return {};
    const { fbp, fbc } = metaIds();
    return {
      'X-Analytics-Visitor': getVisitorId(),
      'X-Analytics-Session': getSession(slug).id,
      // PRAT-209: variação do apelo do cartão, gravada no order_created do checkout.
      'X-Analytics-Card-Cta': resolveCardCtaVariant(),
      ...(fbp ? { 'X-Analytics-Fbp': fbp } : {}),
      ...(fbc ? { 'X-Analytics-Fbc': fbc } : {}),
    };
  } catch {
    return {};
  }
}

/** Reenvia o que ficou na fila de uma visita anterior. Chamar no carregamento. */
export function resendPendingStoreEvents(now = Date.now()) {
  try {
    const pending = outboxRead().filter(
      i => now - i.at < OUTBOX_MAX_AGE_MS && i.tries < OUTBOX_MAX_TRIES
    );
    const retry = pending.map(i => ({ ...i, tries: i.tries + 1 }));
    write(localStorage, OUTBOX_KEY, retry);
    retry.forEach(deliver);
  } catch {
    // analytics nunca derruba o app
  }
}

/** Envia o que está na fila, agrupado por loja e sessão. */
export function flushStoreEvents() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  if (queue.length === 0) return;

  const pending = queue;
  queue = [];
  const visitorId = getVisitorId();
  const meta = metaIds();
  const groups = new Map<string, QueuedEvent[]>();
  for (const item of pending) {
    const key = `${item.slug}|${item.sessionId}`;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  for (const items of groups.values()) {
    for (let i = 0; i < items.length; i += 50) {
      const chunk = items.slice(i, i + 50);
      send(
        chunk[0].slug,
        JSON.stringify({
          visitor_id: visitorId,
          session_id: chunk[0].sessionId,
          attribution: chunk[0].attribution,
          events: chunk.map(item => item.event),
          ...(meta.fbp || meta.fbc ? { meta } : {}),
        })
      );
    }
  }
}

function bindPageHide() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;
  window.addEventListener('pagehide', flushStoreEvents);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushStoreEvents();
  });
}

export type TrackStoreEventOptions = {
  slug?: string;
  orderId?: string;
  props?: Props;
  /** Envia já (pedido criado, saída da página) em vez de esperar o lote. */
  immediate?: boolean;
  /** Id já usado no Pixel; sem ele, um novo. */
  eventId?: string;
};

/** Devolve o id do evento, para o Pixel mandar o mesmo `eventID` (PRAT-83). */
export function trackStoreEvent(
  name: StoreEventName,
  options: TrackStoreEventOptions = {}
): string {
  const eventId = options.eventId || newEventId();
  try {
    const slug = options.slug || currentSlug();
    if (!slug) return eventId;

    bindPageHide();
    const session = getSession(slug);
    queue.push({
      slug,
      sessionId: session.id,
      attribution: session.attribution,
      event: {
        id: eventId,
        name,
        ts: Date.now(),
        path: window.location.pathname.slice(0, 190),
        ...(options.orderId ? { order_id: options.orderId } : {}),
        ...(options.props ? { props: options.props } : {}),
      },
    });

    if (options.immediate) {
      flushStoreEvents();
    } else if (!flushTimer) {
      flushTimer = setTimeout(flushStoreEvents, FLUSH_DELAY_MS);
    }
  } catch {
    // analytics nunca derruba a tela
  }
  return eventId;
}

/** Só para testes. */
export function __resetStoreEventsForTests() {
  queue = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
}
